/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useRef } from "react";
import { getDatabase, child, get, ref, set, onValue, update } from "firebase/database";
import { realtime } from "../../../firebaseConfig";

import { MouseMap } from "./MouseMap";

import "./../Collab/MouseMap.css";

const fakeUsersData = [
  { id: 'fake1', name: 'Dawn', offsetX: 20, offsetY: 20 },
  { id: 'fake2', name: 'Anna', offsetX: -20, offsetY: 30 },
  { id: 'fake3', name: 'Sam', offsetX: 50, offsetY: -20 },
];

const colors = ["#0086b5", "#F3995D", "#FF5C58", "#88C3BF", "#6EB16A", "#F1D365", "#166ca1", "#BE8FB2", "#F4ACB5", "#9D755D", "#D2CEC1"];

const CollabLayer = ({ viewId, lastUpdated, setLastUpdated, isLoading, currentUser, collaborators, components, onUpdate, fakeUsersVisible }) => {
  // console.log("CollabLayer", "components", components[0].id);
  const [collabLayer, setCollabLayer] = useState(null);
  const [isCollabVisible, setIsCollabVisible] = useState(true);
  const [updateCollabListeners, setUpdateCollabListeners] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [lastRendered, setLastRendered] = useState(null);

  const [fakeUserPositions, setFakeUserPositions] = useState(() =>
    fakeUsersData.map(user => ({
      ...user,
      x: Math.random() * window.innerWidth,
      y: 10,
      targetX: 0,
      targetY: 0,
      color: colors[Math.floor(Math.random() * colors.length)]
    }))
  );

  const getCollaboratorColor = useCallback((collaboratorId, collabLayerData) => {
    // console.log("getCollaboratorColor", collabLayerData?.users);
    let colorsAvailable = colors.filter((color) => {
      let available = true;
      for (const [key, value] of Object.entries(collabLayerData?.users)) {
        if (key && value.color === color) available = false;
      }
      return available;
    });

    if (colorsAvailable.length === 0) colorsAvailable = colors;
    const randomIndex = Math.floor(Math.random() * colorsAvailable.length);
    const color = colorsAvailable[randomIndex];
    // console.log("colorsAvailable.length", colorsAvailable.length, "randomIndex", randomIndex, "remove", color);
    // colorsAvailable.splice(randomIndex, 1);
    return color;
  }, []);

  // === fetch the collabLayer ==== \\
  useEffect(() => {
    // Get the intial data for the collabLayer
    const fetchCollabLayer = async () => {
      if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") return;

      let collabLayerData = {};

      const dbRef = ref(getDatabase());
      let collabLayerSnapshot = await get(child(dbRef, `collabLayer/views/${viewId}`));
      if (collabLayerSnapshot.exists()) {
        collabLayerData.users = collabLayerSnapshot.val().users ? collabLayerSnapshot.val().users : {};
        collabLayerData.settings = collabLayerSnapshot.val().settings ? collabLayerSnapshot.val().settings : {};
        collabLayerData.components = collabLayerSnapshot.val().components ? collabLayerSnapshot.val().components : {};
      }

      // Create listeners for all collaborators
      for (let i = 0; i < collaborators.length; i++) {
        const collaborator = collaborators[i];
        const collabEntry = `collabLayer/views/${viewId}/users/${collaborator.id}`;

        // Check if the collaborator is in the collabLayer, if not create an entry
        if (!collabLayerData.users[collaborator.id]) {
          const color = getCollaboratorColor(collaborator.id, collabLayerData);
          const initialData = { mousePctX: 0, mousePctY: 0, color: color };
          await set(ref(realtime, collabEntry), initialData);
          // Refresh the collabLayer data
          collabLayerSnapshot = await get(child(dbRef, `collabLayer/views/${viewId}/users`));
          if (collabLayerSnapshot.exists()) {
            collabLayerData.users = collabLayerSnapshot.val().users ? collabLayerSnapshot.val().users : {};
          }
        }
      }

      setCollabLayer(collabLayerData);
      setUpdateCollabListeners(true);
    };
    if (!collabLayer && !isLoading && collaborators?.length > 0) fetchCollabLayer();
  }, [collabLayer, collaborators, getCollaboratorColor, isLoading, viewId]);

  // === create listeners for collaborator updates ==== \\
  useEffect(() => {
    const handleCollabLayerUpdate = (collaboratorId, data) => {
      const viewPage = document.querySelector(".view-page");
      const relativePosition = data;
      relativePosition.mouseX = Math.round(viewPage.offsetWidth * data.mousePctX);
      relativePosition.mouseY = Math.round(viewPage.offsetHeight * data.mousePctY);

      const scrollY = Math.round(window.scrollY);
      const pageTop = scrollY - viewPage.offsetTop - 15;
      const pageBottom = window.innerHeight - viewPage.offsetTop - 40 + scrollY;

      if (relativePosition.mouseY < pageTop) {
        relativePosition.mouseY = pageTop;
        relativePosition.mouseOffscreen = "top";
      } else if (relativePosition.mouseY > pageBottom) {
        relativePosition.mouseY = pageBottom;
        relativePosition.mouseOffscreen = "bottom";
      } else {
        relativePosition.mouseOffscreen = null;
      }

      const updatedCollabLayer = collabLayer ? { ...collabLayer } : {};
      if (!updatedCollabLayer?.users) updatedCollabLayer.users = {};
      updatedCollabLayer.users = { ...updatedCollabLayer?.users, [collaboratorId]: relativePosition };
      setCollabLayer(updatedCollabLayer);
    };

    const createListenersForCollaborators = () => {
      if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") return;
      setUpdateCollabListeners(false);
      const updatedCollabLayer = collabLayer;

      // Create listeners for all collabLayer users
      Object.keys(updatedCollabLayer.users).forEach((collaboratorId) => {
        const collabEntry = `collabLayer/views/${viewId}/users/${collaboratorId}`;
        const collaboratorRef = ref(realtime, collabEntry);

        // Create listener for collaborator
        onValue(collaboratorRef, async (snapshot) => {
          const realtimeData = snapshot.val();
          if (realtimeData) {
            // Set the color for collaborator if not already set
            if (!realtimeData.color) {
              const color = getCollaboratorColor(collaboratorId, updatedCollabLayer);
              const updatedData = { ...realtimeData, color: color };
              realtimeData.color = color;
              await set(ref(realtime, collabEntry), updatedData);
            }
            handleCollabLayerUpdate(collaboratorId, realtimeData);
          }
        });
      });
      setCollabLayer(updatedCollabLayer);
    };
    if (updateCollabListeners && collabLayer?.users) createListenersForCollaborators();

    // Polling function to check each second if collaborators are online, force re-render
    const checkCollaboratorsOnline = () => {
      setLastRendered(Date.now());
    };
    const interval = setInterval(checkCollaboratorsOnline, 1000);
    return () => clearInterval(interval);
  }, [collabLayer, getCollaboratorColor, isLoading, updateCollabListeners, viewId]);

  // Create listeners for the view updated date
  useEffect(() => {
    const creatViewLastUpdatedListener = () => {
      if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") return;
      const lastUpdatedRef = ref(realtime, `collabLayer/views/${viewId}/settings/lastUpdated`);
      onValue(lastUpdatedRef, (snapshot) => {
        const data = snapshot.val();
        if (data !== null && data !== lastUpdated) {
          setLastUpdated(data);
          onUpdate("last-updated");
        }
      });
    };

    if (collabLayer && lastUpdated === null) {
      creatViewLastUpdatedListener();
    }
  }, [collabLayer, isLoading, lastUpdated, onUpdate, setLastUpdated, viewId]);

  // Create listeners for the editing state of each component
  useEffect(() => {
    const handleComponentEditing = (componentId, data) => {
    // console.log("handleComponentEditing", componentId, data);
    const updatedCollabLayer = collabLayer;
    if (updatedCollabLayer?.components) {
      updatedCollabLayer.components[componentId] = { ...data, hasListener: true };
      setCollabLayer(updatedCollabLayer);
      const activeUser = data?.activeUser ? { ...data.activeUser, color: updatedCollobLayer.users[data.activeUser.uid].color } : null;
      
      if (typeof onUpdate === 'function') {
        onUpdate("component-editing", { componentId: componentId, activeUser: activeUser });
      } else {
        console.warn('CollabLayer: onUpdate is not a function');
      }
    }
    };

    const createListenersForComponents = () => {
      if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") return;
      const updatedCollabLayer = collabLayer;

      // Create listeners for all collabLayer components
      components.forEach((component) => {
        // console.log("check for listener", component.id);
        if (updatedCollabLayer?.components && !updatedCollabLayer?.components[component]?.hasListener) {
          // console.log("create listener for component", component);
          if (!updatedCollabLayer.components[component]) updatedCollabLayer.components[component] = {};
          updatedCollabLayer.components[component].hasListener = true;
          const componentEntry = `collabLayer/views/${viewId}/components/${component}`;
          const componentRef = ref(realtime, componentEntry);

          // Create listener for component
          onValue(componentRef, async (snapshot) => {
            // console.log("component listener fired", component);
            handleComponentEditing(component, snapshot.val());
          });
        } else {
          // console.log("component listener already exists", component.id);
        }
      });
      setCollabLayer(updatedCollabLayer);
    };
    // console.log("collabLayer", collabLayer, "components", components);
    if (collabLayer && components?.length > 0) createListenersForComponents();
  }, [collabLayer, components, isLoading, onUpdate, viewId]);

  // === FAKE USER DEMO LOGIC ===
  useEffect(() => {
    let animationFrameId;

    const updatePositions = () => {
      setFakeUserPositions(prevPositions =>
        prevPositions.map(user => {
          const dx = user.targetX - user.x;
          const dy = user.targetY - user.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 1) {
            return { ...user, x: user.targetX, y: user.targetY };
          }

          return { ...user, x: user.x + dx * 0.1, y: user.y + dy * 0.1 };
        })
      );
      animationFrameId = requestAnimationFrame(updatePositions);
    };

    if (fakeUsersVisible) {
      animationFrameId = requestAnimationFrame(updatePositions);
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [fakeUsersVisible, setFakeUserPositions]);

  useEffect(() => {
    if (fakeUsersVisible) {
      setFakeUserPositions(prevPositions =>
        prevPositions.map(user => ({
          ...user,
          targetX: mousePos.x + user.offsetX,
          targetY: mousePos.y + user.offsetY
        }))
      );
    }
  }, [fakeUsersVisible, mousePos, setFakeUserPositions]);

  useEffect(() => {
    if (fakeUsersVisible) {
      const interval = setInterval(() => {
        setFakeUserPositions(prevPositions =>
          prevPositions.map(user => {
            const randomX = (Math.random() - 0.75) * 600;
            const randomY = (Math.random() - 0.75) * 600;
            const viewPage = document.querySelector(".view-page");
            const newTargetX = user.x + randomX;
            const newTargetY = user.y + randomY;

            return {
              ...user,
              targetX: Math.max(0, Math.min(newTargetX, viewPage?.offsetWidth ?? 0)),
              targetY: Math.max(0, Math.min(newTargetY, viewPage?.offsetHeight ?? 0))
            };
          })
        );
      }, Math.floor(Math.random() * 7000) + 3000);

      return () => clearInterval(interval);
    }
  }, [fakeUsersVisible, setFakeUserPositions]);
  // === END FAKE USER DEMO LOGIC ===

  // === send user mouse position to Firebase ==== \\
  useEffect(() => {
    const viewPage = document.querySelector(".view-page");

    const updateCollabLayerPosition = (userId, xPct, yPct) => {
      if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") return;

      const collaboratorData = {
        mousePctX: xPct,
        mousePctY: yPct,
        lastActive: new Date().getTime(),
      };
      const dbRef = getDatabase();
      update(ref(dbRef, `collabLayer/views/${viewId}/users/${userId}`), collaboratorData);
    };

    const handleMouseMove = (event) => {
      const { pageX, pageY } = event;
      const mouseOffsetTop = viewPage.offsetTop;
      const pageWidth = viewPage.offsetWidth;
      const pageHeight = viewPage.offsetHeight;
      const mouseX = pageX;
      const mouseY = pageY - mouseOffsetTop;
      const mousePctX = pageX / pageWidth;
      const mousePctY = (pageY - mouseOffsetTop) / pageHeight;

      if (mouseX !== mousePos.x || mouseY !== mousePos.y) {
        setMousePos({ x: mouseX, y: mouseY });
        updateCollabLayerPosition(currentUser.uid, mousePctX, mousePctY);
      }
    };

    viewPage.addEventListener("mousemove", handleMouseMove);

    return () => {
      viewPage.removeEventListener("mousemove", handleMouseMove);
    };
  }, [currentUser.uid, mousePos.x, mousePos.y, setMousePos, viewId]); // Optimized dependency array

  // === show/hide collaborators ==== \\
  useEffect(() => {
    const handleCollabPress = (event) => {
      if (event.metaKey && event.key === "e") {
        setIsCollabVisible(!isCollabVisible);
      }
    };

    document.addEventListener("keydown", handleCollabPress);
    return () => {
      document.removeEventListener("keydown", handleCollabPress);
    };
  }, [isCollabVisible]);

  return (
    <div className="collabLayer" key={lastRendered}>
      {/* FAKE USER DEMO RENDER */}
      {fakeUsersVisible && fakeUserPositions.map(user => (
        <MouseMap
          key={user.id}
          posX={user.x}
          posY={user.y}
          color={user.color}
          opacity={1}
        >
          {user.name}
        </MouseMap>
      ))}
      {/* END FAKE USER DEMO RENDER */}
      {collaborators?.map((collaborator) => {
        const collabLayerUser = collabLayer?.users[collaborator.id];
        const currentTime = new Date().getTime();
        const isOnline = collabLayerUser?.lastActive ? collabLayerUser.lastActive > currentTime - 5000 : false;
        return collaborator.isCurrentUser ? null : (
          <MouseMap
            key={collaborator.id}
            id={collaborator.id}
            posX={collabLayer?.users[collaborator.id]?.mouseX}
            posY={collabLayer?.users[collaborator.id]?.mouseY}
            color={collabLayer?.users[collaborator.id]?.color ? collabLayer?.users[collaborator.id]?.color : null}
            opacity={isOnline ? 1 : 0}
            offscreen={collabLayer?.users[collaborator.id]?.mouseOffscreen}
          >
            {collaborator.firstName ? collaborator.firstName : collaborator.name ? collaborator.name : "Anonymous"}
          </MouseMap>
        );
      })}
    </div>
  );
};

export default CollabLayer;
